import React, { useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  getDurationPackageOptions,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'

import FlashHotelRoomOption from './FlashHotelRoomOption'
import Caption from 'LuxKit/Typography/Caption'
import { buildRoomPackageOptionsViewOrder, filterPackageOptionsBySkiPackageType, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { SecureWithDepositBanner } from 'components/Common/SecureWithDeposit/SecureWithDepositBanner'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import GeoContext from 'contexts/geoContext'
import config from 'constants/config'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import ReserveForZeroPaymentBanner from 'components/Common/ReserveForZeroPaymentBanner/ReserveForZeroPaymentBanner'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import moment from 'moment'
import { max } from 'lib/array/arrayUtils'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.Offer;
  showFlightInformation?: boolean;
  destinationAirport?: App.Airport;
  packageOptions: Array<OfferPackageOptionView>;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  checkIn?: string;
  airport?: App.Airport;
  isSpoofed: boolean;
  defaultDepositAmountPercentage: number;
  onStickyFooterToggle: (visible: boolean) => void;
}

function FlashHotelPackageOptions(props: Props) {
  const {
    showFlightInformation,
    destinationAirport,
    offer,
    // TEMP: Remove this logic when "006RF00000IRdvlYAD" become unavailable
    packageOptions: basePackageOptions,
    cheapestOption,
    cheapestSelectedOption,
    checkIn,
    isSpoofed,
    defaultDepositAmountPercentage,
    airport,
    onStickyFooterToggle,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  // TEMP: Remove this logic when "006RF00000IRdvlYAD" become unavailable
  const { packageOptions, skiPackageFiltered } = useMemo(() => {
    return filterPackageOptionsBySkiPackageType(basePackageOptions, cheapestSelectedOption)
  }, [basePackageOptions, cheapestSelectedOption])

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageOptions, checkIn])

  const { currentCurrency } = useContext(GeoContext)
  const packageView = cheapestSelectedOption ?? cheapestOption
  const { hotelTotal } = packageView
  const showDealBanner = airport && destinationAirport
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotal)
  const reserveForZeroAvailable = isSpoofed && isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const packageStartRef = useRef<HTMLDivElement>(null)

  const reachedAnchor = useHasGotToAnchor(packageStartRef?.current, -110, 25)

  const activeBanner = useMemo(() => {
    if (reserveForZeroAvailable) {
      return <ReserveForZeroPaymentBanner />
    }
    if (depositsAvailable && !reserveForZeroAvailable && config.DEPOSITS_TAGS_ENABLED) {
      return <SecureWithDepositBanner
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />
    }
    if (showDealBanner && config.FLIGHT_ENABLED) {
      return <OfferPageFlightDealBanner
        destinationAirportCode={destinationAirport.code}
        originAirportCode={airport.code}
      />
    }
  }, [airport, depositAmountPercentage, depositsAvailable, destinationAirport, offer, reserveForZeroAvailable, showDealBanner])

  useEffect(() => {
    onStickyFooterToggle(!reachedAnchor)
  }, [onStickyFooterToggle, reachedAnchor])

  return (
    <VerticalSpacer gap={16}>
      <LayoutContainer>
        {activeBanner}
      </LayoutContainer>

      <PackagesLayoutContainer>
        <VerticalSpacer gap={32} ref={packageStartRef}>
          {packageOptionsByRoom.map((item) =>
            <FlashHotelRoomOption
              key={item.roomId}
              offer={offer}
              packageViews={item.packageViews}
              flightDestination={destinationAirport}
              availableRoomCount={availableRoomCountByRoomId.get(item.roomId)}
              useSkiDealAvailabilityActions={skiPackageFiltered}
            />,
          )}
        </VerticalSpacer>
      </PackagesLayoutContainer>

      <LayoutContainer>
        {showFlightInformation && <Caption variant="medium" align="center">
          *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
        </Caption>}
      </LayoutContainer>
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  offer: state.offer!,
  selectedDuration: state.duration,
  checkIn: state.checkInDate,
  airport: state.airport,
  packageOptions: getDurationPackageOptions(state, appState),
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  open: !!state.activeFilter,
}), FlashHotelPackageOptions, preventRenderWithOpenFilter)
